import React, { useState, useEffect, useRef, useCallback } from 'react';
import classNames from 'classnames';
import { useAuth } from '../context/AuthContext';
import { environment } from './util/baseUrl';
import { Button } from 'primereact/button';
import { Dialog } from 'primereact/dialog';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Toast } from 'primereact/toast';
import { UsuarioEntity } from '../entity/UsuarioEntity';
import DialogHeader from '../components/ui/DialogHeader';
import './Perfil.css';

const Perfil = () => {
    const { axiosInstance, user, updateUserProfile } = useAuth();
    const [activeTab, setActiveTab] = useState('personal');
    const [data, setData] = useState(UsuarioEntity);
    const [submitted, setSubmitted] = useState(false);
    const [EntidadNewDialog, setEntidadNewDialog] = useState(false);
    const [EntidadDialog, setEntidadDialog] = useState(false);
    const [product, setProduct] = useState(UsuarioEntity);
    const [passw, setPassw] = useState({ id: null, oldPassword: '', newPassword: '' });
    const [confirmPassword, setConfirmPassword] = useState('');
    const toast = useRef(null);
    const fileInputRef = useRef(null);
    const [imagePreview, setImagePreview] = useState(null);



    const formatDateTime = (value) => {
        if (!value) return 'No disponible';
        const date = new Date(value);
        if (Number.isNaN(date.getTime())) return 'No disponible';
        return date.toLocaleString('es-PE');
    };

    const peticionGet = useCallback(async () => {
        const userId = user?.id;
        if (!userId) {
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'ID de usuario no encontrado.', life: 5000 });
            return;
        }

        try {
            const response = await axiosInstance.get(`/api/usuario/${userId}`);
            const raw = response.data || {};
            const mapped = {
                ...raw,
                apellidoPaterno: raw.apellidoPaterno || '',
                apellidoMaterno: raw.apellidoMaterno || '',
                docIdentidad: raw.docIdentidad || raw.numeroDocumento || ''
            };
            setData(mapped);
            setProduct(mapped);
        } catch (error) {
            console.error("Error al cargar datos de usuario:", error);
            if (error.response?.status === 403) {
                toast.current.show({ severity: 'error', summary: 'Error de permisos', detail: 'No tienes permisos para ver este perfil.', life: 5000 });
            } else {
                toast.current.show({ severity: 'error', summary: 'Error', detail: 'No se pudieron cargar los datos del usuario.', life: 5000 });
            }
        }
    }, [axiosInstance, user]);

    const peticionPut = async () => {
        try {
            await axiosInstance.post('/api/usuario/changepassword', {
                id: user.id,
                oldPassword: passw.oldPassword,
                newPassword: passw.newPassword
            });
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Contraseña cambiada correctamente.', life: 3000 });
            setEntidadNewDialog(false);
            setPassw({ id: null, oldPassword: '', newPassword: '' });
            setConfirmPassword('');
        } catch (error) {
            console.error("Error al cambiar contraseña:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Datos incorrectos o error al cambiar la contraseña.', life: 5000 });
        }
    };

    const peticionPutMod = async () => {
        try {
            const payload = {
                id: product.id,
                username: product.username,
                nombres: product.nombres,
                apellidoPaterno: product.apellidoPaterno || '',
                apellidoMaterno: product.apellidoMaterno || '',
                email: product.email,
                telefono: product.telefono,
                tipoDocumento: product.tipoDocumento,
                numeroDocumento: product.numeroDocumento || product.docIdentidad,
                idSucursal: product.idSucursal
            };

            await axiosInstance.put('/api/usuario/', payload);
            toast.current.show({ severity: 'success', summary: 'Éxito', detail: 'Datos actualizados correctamente.', life: 3000 });
            setEntidadDialog(false);
            await peticionGet();
        } catch (error) {
            console.error("Error al actualizar datos:", error);
            toast.current.show({ severity: 'error', summary: 'Error', detail: 'Datos incorrectos o error al actualizar.', life: 5000 });
        }
    };

    const hideDialogNew = () => {
        setSubmitted(false);
        setEntidadNewDialog(false);
    };

    const hideDialog = () => {
        setSubmitted(false);
        setEntidadDialog(false);
    };

    const openNew = () => {
        setPassw({ id: user?.id, oldPassword: '', newPassword: '' });
        setEntidadNewDialog(true);
    };

    const open = () => {
        setSubmitted(false);
        setEntidadDialog(true);
    };

    const saveProduct = () => {
        setSubmitted(true);
        if (EntidadNewDialog) {
            if (passw.oldPassword.trim() && passw.newPassword.trim() && confirmPassword.trim()) {
                if (passw.newPassword === confirmPassword) {
                    peticionPut();
                } else {
                    toast.current.show({ severity: 'error', summary: 'Error de validación', detail: 'Las contraseñas no coinciden.', life: 3000 });
                }
            }
        } else if (EntidadDialog) {
            const telefonoValido = /^\d{9}$/.test((product.telefono || '').trim());
            if (!telefonoValido) {
                toast.current.show({ severity: 'error', summary: 'Error de validación', detail: 'El teléfono debe tener 9 dígitos numéricos.', life: 3000 });
                return;
            }

            if (product.email && product.telefono && product.apellidoPaterno && product.apellidoMaterno) {
                peticionPutMod();
            }
        }
    };

    // Funciones para manejar imagen de perfil
    const handleImageClick = () => {
        fileInputRef.current?.click();
    };

    // Validar magic bytes para asegurar que es una imagen real
    const validateImageBytes = (file) => {
        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = (e) => {
                const arr = (new Uint8Array(e.target.result)).subarray(0, 4);
                let header = "";
                for (let i = 0; i < arr.length; i++) {
                    header += arr[i].toString(16).toUpperCase();
                }

                let isValid = false;
                // JPEG: FF D8 FF
                if (header.startsWith("FFD8FF")) isValid = true;
                // PNG: 89 50 4E 47
                else if (header === "89504E47") isValid = true;
                // GIF: 47 49 46 38
                else if (header === "47494638") isValid = true;
                // WebP: 52 49 46 46 (RIFF....WEBP) - simplificado
                // Para WebP necesitamos mirar bytes específicos, pero el backend hace validación completa.
                // Aquí aceptamos si parece RIFF
                else if (header === "52494646") isValid = true;

                resolve(isValid);
            };
            reader.onerror = () => reject(new Error("Error leyendo archivo"));
            reader.readAsArrayBuffer(file);
        });
    };

    const handleImageChange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validar tipo de archivo (validación básica por extensión)
        const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
        if (!allowedTypes.includes(file.type)) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Solo se permiten imágenes (JPG, PNG, GIF, WEBP)',
                life: 5000
            });
            return;
        }

        // Validar tamaño (máximo 5MB)
        if (file.size > 5 * 1024 * 1024) {
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: 'La imagen no debe superar los 5MB',
                life: 5000
            });
            return;
        }

        // Validar Magic Bytes (validación profunda)
        try {
            const isValidImage = await validateImageBytes(file);
            if (!isValidImage) {
                toast.current.show({
                    severity: 'error',
                    summary: 'Archivo Inválido',
                    detail: 'El archivo no es una imagen válida o está dañado.',
                    life: 5000
                });
                return;
            }
        } catch (error) {
            console.error("Error validando imagen:", error);
            return;
        }

        // Crear preview
        const reader = new FileReader();
        reader.onloadend = () => {
            setImagePreview(reader.result);
        };
        reader.readAsDataURL(file);

        // Subir imagen
        uploadImage(file);
    };

    const uploadImage = async (file) => {
        const formData = new FormData();
        formData.append('file', file);
        try {
            const response = await axiosInstance.post('/api/usuario/upload-profile-image', formData);

            toast.current.show({
                severity: 'success',
                summary: 'Éxito',
                detail: 'Imagen de perfil actualizada correctamente',
                life: 3000
            });

            // Actualizar el estado con la nueva URL de imagen
            setData(prev => ({ ...prev, profileImage: response.data.imageUrl }));

            // Actualizar el contexto para que el topbar también muestre la imagen
            updateUserProfile({ profileImage: response.data.imageUrl });

            setImagePreview(null);
        } catch (error) {
            console.error("Error al subir imagen:", error);
            toast.current.show({
                severity: 'error',
                summary: 'Error',
                detail: error.response?.data?.message || 'Error al subir la imagen',
                life: 5000
            });
            setImagePreview(null);
        } finally {
            // Limpiar input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const getProfileImageUrl = () => {
        if (imagePreview) return imagePreview;
        if (data.profileImage) {
            // Si es una URL relativa, construir la URL completa
            if (data.profileImage?.startsWith('/')) {
                return `${environment.backendUrl}${data.profileImage}`;
            }
            return data.profileImage;
        }
        return null;
    };

    const onInputChangePass = (e, name) => {
        const val = e.target.value || '';
        if (name === 'confirmPassword') {
            setConfirmPassword(val); // Actualiza el string directamente
        } else {
            setPassw(prev => ({ ...prev, [name]: val })); // Actualiza el objeto passw
        }
    };

    const onInputChange = (e, name) => {
        let val = e.target.value || '';
        if (name === 'telefono') {
            val = val.replace(/[^0-9]/g, '').slice(0, 9);
        }
        setProduct(prev => ({ ...prev, [name]: val }));
    };

    const productDialogFooterNew = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialogNew} />
            <Button label="Aceptar" icon="pi pi-check" onClick={saveProduct} />
        </>
    );

    const productDialogFooter = (
        <>
            <Button label="Cancelar" icon="pi pi-times" className="p-button-text p-button-secondary" onClick={hideDialog} />
            <Button label="Aceptar" icon="pi pi-check" onClick={saveProduct} />
        </>
    );

    useEffect(() => {
        peticionGet();
    }, [peticionGet]);

    return (
        <div className="grid perfil-page">
            <Toast ref={toast} />
            <div className="col-12">
                <div className="perfil-header border-round-xl mb-4 p-4 text-center">
                    <div
                        className="profile-image-container inline-block relative cursor-pointer"
                        onClick={handleImageClick}
                        title="Haz clic para cambiar tu foto de perfil"
                    >
                        {getProfileImageUrl() ? (
                            <img
                                src={getProfileImageUrl()}
                                alt="Foto de perfil"
                                className="profile-image"
                            />
                        ) : (
                            <div className="profile-image-placeholder">
                                <i className="pi pi-user"></i>
                            </div>
                        )}
                        <div className="profile-image-overlay">
                            <i className="pi pi-camera"></i>
                            <span>Cambiar foto</span>
                        </div>
                        <input
                            type="file"
                            ref={fileInputRef}
                            onChange={handleImageChange}
                            accept="image/jpeg,image/png,image/gif,image/webp"
                            style={{ display: 'none' }}
                        />
                    </div>
                    <h1 className="perfil-title">Mi Perfil</h1>
                    <p className="perfil-subtitle">Gestiona tu información personal y configuración</p>
                </div>

                <div className="perfil-card">
                    <div className="section-tabs">
                        <button
                            className={`section-tab ${activeTab === 'personal' ? 'active' : ''}`}
                            onClick={() => setActiveTab('personal')}
                        >
                            <i className="pi pi-id-card" style={{ marginRight: '0.5rem' }}></i>
                            Datos Personales
                        </button>
                        <button
                            className={`section-tab ${activeTab === 'security' ? 'active' : ''}`}
                            onClick={() => setActiveTab('security')}
                        >
                            <i className="pi pi-shield" style={{ marginRight: '0.5rem' }}></i>
                            Seguridad
                        </button>
                        {/* Espacio para futuras pestañas según el rol */}
                    </div>

                    <div className="section-content">
                        {activeTab === 'personal' && (
                            <>
                                <div className="data-section">
                                    <div className="section-title">
                                        <i className="pi pi-user"></i>
                                        Información Personal
                                    </div>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-id-card"></i>
                                                Nombres
                                            </div>
                                            <div className="info-value">{data.nombres}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-user"></i>
                                                Apellido Paterno
                                            </div>
                                            <div className="info-value">{data.apellidoPaterno}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-user"></i>
                                                Apellido Materno
                                            </div>
                                            <div className="info-value">{data.apellidoMaterno}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-file"></i>
                                                N° Documento
                                            </div>
                                            <div className="info-value">{data.docIdentidad}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="data-section">
                                    <div className="section-title">
                                        <i className="pi pi-envelope"></i>
                                        Información de Contacto
                                    </div>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-at"></i>
                                                Correo Electrónico
                                            </div>
                                            <div className="info-value">{data.email}</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-phone"></i>
                                                Teléfono
                                            </div>
                                            <div className="info-value">{data.telefono}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="data-section">
                                    <div className="section-title">
                                        <i className="pi pi-briefcase"></i>
                                        Información del Sistema
                                    </div>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-user-plus"></i>
                                                Usuario
                                            </div>
                                            <div className="info-value">
                                                {data.username}
                                            </div>
                                        </div>
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-shield"></i>
                                                Rol/Permisos
                                            </div>
                                            <div className="info-value">
                                                <span className="perfil-badge">{data.profile?.name || data.role || user?.role || 'SIN ROL'}</span>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="perfil-actions">
                                    <Button
                                        label="Actualizar Datos"
                                        icon="pi pi-pencil"
                                        className="p-button-outlined p-button-success"
                                        onClick={open}
                                    />
                                </div>
                            </>
                        )}

                        {activeTab === 'security' && (
                            <>
                                <div className="data-section">
                                    <div className="section-title">
                                        <i className="pi pi-lock"></i>
                                        Contraseña y Seguridad
                                    </div>
                                    <div className="info-grid">
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-key"></i>
                                                Contraseña
                                            </div>
                                            <div className="info-value">••••••••</div>
                                        </div>
                                        <div className="info-item">
                                            <div className="info-label">
                                                <i className="pi pi-calendar"></i>
                                                Última actualización
                                            </div>
                                            <div className="info-value">{formatDateTime(data.fechaEdicion)}</div>
                                        </div>
                                    </div>
                                </div>

                                <div className="perfil-actions">
                                    <Button
                                        label="Cambiar Contraseña"
                                        icon="pi pi-lock"
                                        className="p-button-outlined p-button-success"
                                        onClick={openNew}
                                    />
                                </div>
                            </>
                        )}
                    </div>
                </div>

                <Dialog
                    visible={EntidadNewDialog}
                    style={{ width: '450px' }}
                    header={
                        <DialogHeader
                            title="Cambiar Contraseña"
                            subtitle="Actualiza tus credenciales de acceso"
                            icon="pi pi-lock"
                        />
                    }
                    modal
                    className="p-fluid custom-profile-dialog"
                    footer={productDialogFooterNew}
                    onHide={hideDialogNew}
                >
                    <div className="field">
                        <label htmlFor="oldPassword" className="password-field-label">Contraseña Actual</label>
                        <Password
                            id="oldPassword"
                            name="oldPassword"
                            value={passw.oldPassword}
                            onChange={(e) => onInputChangePass(e, 'oldPassword')}
                            toggleMask
                            feedback={false}
                            placeholder="Ingrese su contraseña actual"
                            required
                            autoFocus
                            className={classNames('password-input-field', { 'p-invalid': submitted && !passw.oldPassword })}
                        />
                        {submitted && !passw.oldPassword && <small className="p-invalid block mt-1">La contraseña actual es requerida.</small>}
                    </div>
                    <div className="field">
                        <label htmlFor="newPassword" className="password-field-label">Nueva Contraseña</label>
                        <Password
                            id="newPassword"
                            name="newPassword"
                            value={passw.newPassword}
                            onChange={(e) => onInputChangePass(e, 'newPassword')}
                            toggleMask
                            feedback={false}
                            placeholder="Ingrese su nueva contraseña"
                            required
                            className={classNames('password-input-field', { 'p-invalid': submitted && !passw.newPassword })}
                        />
                        {submitted && !passw.newPassword && <small className="p-invalid block mt-1">La nueva contraseña es requerida.</small>}
                    </div>
                    <div className="field">
                        <label htmlFor="confirmPassword" className="password-field-label">Confirmar Contraseña</label>
                        <Password
                            id="confirmPassword"
                            name="confirmPassword"
                            value={confirmPassword}
                            onChange={(e) => onInputChangePass(e, 'confirmPassword')}
                            toggleMask
                            feedback={false}
                            placeholder="Confirme su nueva contraseña"
                            required
                            className={classNames('password-input-field', { 'p-invalid': submitted && !confirmPassword })}
                        />
                        {submitted && !confirmPassword && <small className="p-invalid block mt-1">La confirmación de contraseña es requerida.</small>}
                        {submitted && passw.newPassword !== confirmPassword && <small className="p-invalid block mt-1">Las contraseñas no coinciden.</small>}
                    </div>
                </Dialog>
                <Dialog
                    visible={EntidadDialog}
                    style={{ width: '500px' }}
                    header={
                        <DialogHeader
                            title="Datos de Usuario"
                            subtitle="Modifica tu información de contacto"
                            icon="pi pi-user-edit"
                        />
                    }
                    modal
                    className="p-fluid custom-profile-dialog"
                    footer={productDialogFooter}
                    onHide={hideDialog}
                >
                    <div className="formgrid grid">
                        <div className="field col-12 md:col-12">
                            <label htmlFor="docIdentidad">Doc. de Identidad</label>
                            <InputText id="docIdentidad" name="docIdentidad" value={product.docIdentidad} disabled />
                        </div>
                    </div>
                    <div className="formgrid grid">
                        <div className="field col-12 md:col-12">
                            <label htmlFor="nombre">Nombres</label>
                            <InputText id="nombres" name="nombres" value={product.nombres} onChange={(e) => onInputChange(e, 'nombres')} required disabled className={classNames({ 'p-invalid': submitted && !product.nombres })} />
                            {submitted && !product.nombres && <small className="p-invalid">El nombre es requerido.</small>}
                        </div>
                    </div>
                    <div className="formgrid grid">
                        <div className="field col-12 md:col-6">
                            <label htmlFor="apellidoPaterno">Apellido Paterno</label>
                            <InputText id="apellidoPaterno" name="apellidoPaterno" value={product.apellidoPaterno} onChange={(e) => onInputChange(e, 'apellidoPaterno')} required disabled className={classNames({ 'p-invalid': submitted && !product.apellidoPaterno })} />
                            {submitted && !product.apellidoPaterno && <small className="p-invalid">El apellido paterno es requerido.</small>}
                        </div>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="apellidoMaterno">Apellido Materno</label>
                            <InputText id="apellidoMaterno" name="apellidoMaterno" value={product.apellidoMaterno} onChange={(e) => onInputChange(e, 'apellidoMaterno')} required disabled className={classNames({ 'p-invalid': submitted && !product.apellidoMaterno })} />
                            {submitted && !product.apellidoMaterno && <small className="p-invalid">El apellido materno es requerido.</small>}
                        </div>
                    </div>
                    <div className="formgrid grid">
                        <div className="field col-6">
                            <label htmlFor="email">Correo</label>
                            <InputText id="email" name="email" value={product.email} onChange={(e) => onInputChange(e, 'email')} required className={classNames({ 'p-invalid': submitted && !product.email })} />
                            {submitted && !product.email && <small className="p-invalid">El correo es requerido.</small>}
                        </div>
                        <div className="field col-12 md:col-6">
                            <label htmlFor="telefono">Teléfono</label>
                            <InputText id="telefono" name="telefono" value={product.telefono} onChange={(e) => onInputChange(e, 'telefono')} required className={classNames({ 'p-invalid': submitted && !product.telefono })} />
                            {submitted && !product.telefono && <small className="p-invalid">El teléfono es requerido.</small>}
                        </div>
                    </div>
                </Dialog>
            </div>
        </div>
    );
};

export default Perfil;